/**
 * Created by fers on 26.09.15.
 */

import { v4 as uuidv4 } from 'uuid';
import { declareLinkedEntity, userSupportedTypes } from '../fw/MetaDataHelper';
import * as Permission from '../util/Permission';
import { ACCESS_TYPE_DEVELOPER, ACCESS_TYPE_PARTNER1, ACCESS_TYPE_PARTNER2, ACCESS_TYPE_REGULARUSER } from './UserType';

export default declareLinkedEntity({
    entity: "flowOptionDefs",
    graphQL: "FlowOptionDef",
    name: "Flow Option Definition",
    defaultOrder: ['order', 'name'],
    writePermission: [
        Permission.PERM_EDIT_CHANNEL_DEF
    ],
    dynamoPrefix: 'def',
    storageType: 'schema',
    parentRelation: 'flowOptionDefs',
    defaultTitle: (data) => {
        return data['name'];
    },
    onModify: ['flowDef'],
    fields: (types) => { return {
        id: {
            name: "Flow Option Definition ID",
            type: "id",
            defaultValue: () => {
                return uuidv4();
            },
            primaryKey: true,
            required: true,
            compositeKey: true,
            userEditable: false,
        },
        flowDef: {
            name: "Flow Definition",
            type: "relation",
            to: () => types['flowDefs'],
            defaultValue: (flowDef) => {
                return flowDef['id'];
            },
            required: true,
            parent: true,
            compositeKey: true,
            hiddenCreate: true,
            relativeTo: ["flowDefVersion"],
        },
        flowDefVersion: {
            name: "Flow Definition Version",
            to: () => types['flowDefVersions'],
            type: "relation",
            defaultValue: (flow) => {
                return flow['flowDefVersion_id'];
            },
            required: true,
            hiddenCreate: true,
            compositeKey: true,
        },
        order: {
            name: "Order",
            type: "int",
            defaultValue: "0",
        },
        required: {
            name: "Required",
            type: "boolean",
            defaultValue: false,
            visibility: (data) => { return !data['disabled']; }
        },
        disabled: {
            name: "Disabled",
            type: "boolean",
            defaultValue: false,
        },
        key: {
            name: "Key",
            type: "string",
            trim: true,
            defaultValue: "",
            required: true,
            validations: {
                matchRegexp: /^[a-zA-Z0-9_]+$/,
            }
        },
        name: {
            name: "Name",
            type: "string",
            trim: true,
            translatable: true,
            defaultValue: "",
            required: true,
        },
        prefix: {
            name: "Prefix",
            type: "string",
            trim: true,
            translatable: true,
            description: "It is part of input field and suggest what user should add (eg. https://[user entered data].example.com/).",
            defaultValue: "",
            visibility: (data) => { return data['type'] !== 'form' && data['type'] !== 'enum' && data['type'] !== 'enums'  && data['type'] !== 'dictionary'; },
        },
        suffix: {
            name: "Suffix",
            type: "string",
            trim: true,
            translatable: true,
            description: "It is part of input field and suggest what user should add (eg. https://[user entered data].example.com/).",
            defaultValue: "",
            visibility: (data) => { return data['type'] !== 'form' && data['type'] !== 'enum' && data['type'] !== 'enums'  && data['type'] !== 'dictionary'; },
        },
        examples: {
            name: "Examples",
            type: "string",
            translatable: true,
            trim: true,
            defaultValue: "",
            visibility: (data) => { return data['type'] !== 'form'; }
        },
        defaultValue: {
            name: "Default Value",
            type: "string",
            translatable: true,
            trim: true,
            defaultValue: "",
            visibility: (data) => { return data['type'] !== 'form'; }
        },
        defaultValueForExisting: {
            name: "Default Value for existing flows",
            type: "string",
            translatable: true,
            trim: true,
            defaultValue: "",
            visibility: (data) => { return data['type'] !== 'form'; }
        },
        description: {
            name: "Description",
            type: "text",
            translatable: true,
            defaultValue: ""
        },
        longDescription: {
            name: "Long Description",
            type: "html",
            translatable: true,
            defaultValue: ""
        },
        isAuthIdentity: {
            name: "Is authentication identity",
            type: "boolean",
            description: "Is this part of user authorization?",
            defaultValue: false,
            visibility: (data) => { return data['type'] !== 'form'; },
        },
        isAuthInfo: {
            name: "Is authentication info",
            type: "boolean",
            description: "Should this option be stored in safe store for authentication data",
            defaultValue: false,
            visibility: (data) => { return data['isAuthIdentity'] || data['isAuthInfo']; }
        },
        skipOnCreate: {
            name: "Skip on channel create",
            type: "boolean",
            defaultValue: false,
            visibility: (data) => { return !data['disabled']; }
        },
        accessType: {
            name: "Access Type",
            type: "enum",
            visibility: (data) => { return !data['disabled']; },
            values: {
                [ACCESS_TYPE_REGULARUSER]: {
                    name: "Regular User",
                },
                [ACCESS_TYPE_PARTNER1]: {
                    name: "Partner 1",
                },
                [ACCESS_TYPE_PARTNER2]: {
                    name: "Partner 2",
                },
                [ACCESS_TYPE_DEVELOPER]: {
                    name: "Developers Only",
                },
            },
            defaultValue: ACCESS_TYPE_REGULARUSER,
        },
        developersOnly: {
            name: "Visible only for developers",
            type: "boolean",
            defaultValue: false,
            visibility: (data) => { return false; }
        },
        partner1Only: {
            name: "Visible only for Partner 1",
            type: "boolean",
            defaultValue: false,
            visibility: (data) => { return false; }
        },
        partner2Only: {
            name: "Visible only for Partner 2",
            type: "boolean",
            defaultValue: false,
            visibility: (data) => { return false; }
        },
        type: {
            name: "Type",
            type: "enum",
            defaultValue: "string",
            values: {...userSupportedTypes,     "form": {
                    "name": "Form",
                },
            },
        },
        regexp: {
            name: "RegExp Validation",
            description: "Validation must contain ^$. Example: ^[a-zA-Z0-9\-]+$",
            type: "string",
            defaultValue: "",
            visibility: (data) => { return data['type'] !== 'form'; }
        },
        enumValues: {
            name: "Enum Values",
            type: "object",
            visibility: (data) => { return data['type'] === 'enum' || data['type'] === 'enums'; },
            descriptionHtml: "<small>Example: <pre>{    \"CZ\": {\n        \"name\": {\n            \"en\": \"Czech\",\n            \"cs\": \"Česko\"\n        }\n    },\n    \"EN\": {\n        \"name: {\n            \"en\": \"English\",\n            \"cs\": \"Česky\"\n        }\n    }}</pre></small>",
        },
        fileContentTypes: {
            name: "File content types",
            type: "string",
            visibility: (data) => { return data['type'] === 'file' || data['type'] === 'certificate' || data['type'] === 'image'; }
        },
        datiQuery: {
            name: "DatiQuery",
            type: "string",
            visibility: (data) => { return data['type'] === 'enum' || data['type'] === 'enums' || data['type'] === 'dictionary'; },
            descriptionHtml: "<small>Example: <pre>SELECT current.\"com.dativery.model.sharedFields.code@code\" as code, current.\"com.dativery.model.sharedFields.name@name\" as label FROM [APP $CURRENT TYPE \"eu.flexibee.c\"] as current</pre></small>",
        },
        datiQueryHasParameters: {
            name: "DatiQuery has parameters",
            type: "boolean",
            visibility: (data) => { return data['type'] === 'enum' || data['type'] === 'enums'; },
        },
        datiQuery2: {
            name: "DatiQuery 2",
            type: "string",
            visibility: (data) => { return data['type'] === 'dictionary'; },
        },
        flowChannelDef: {
            name: "Application Definition",
            to: () => types['flowChannelDefs'],
            type: "relation",
            visibility: (data) => { return data['type'] === 'enum' || data['type'] === 'enums' || data['type'] === 'dictionary'; },
            relativeTo: ["flowDef"],
        },
        flowChannelDef2: {
            name: "Application Definition",
            to: () => types['flowChannelDefs'],
            type: "relation",
            visibility: (data) => { return data['type'] === 'dictionary'; },
            relativeTo: ["flowDef"],
        },
        flowOptionGroupDef: {
            name: "Option Group",
            to: () => types['flowOptionGroupDefs'],
            type: "relation",
            relativeTo: ["flowDef"],
        },
        visibilityJS: {
            name: "Visibility JS",
            type: "javascript",
            defaultValue: "",
            globalVariables: {
                'getOptionValue': 'getValue(field: string)',
                'setTitle': 'setTitle(title: string)',
                'hasChannelDef': 'hasChannelDef(keyOrChannelDefId: string, alias: string)',
            },
        },
        filterValuesJS: {
            name: "Filter Values JS",
            type: "javascript",
            defaultValue: "",
            globalVariables: {
                'getOptionValue': 'getOptionValue(field: string)',
                'hasChannelDef': 'hasChannelDef(keyOrChannelDefId: string, alias: string)',
                'json': 'json: Map<String,String>',
            },
            visibility: (data) => { return data['type'] === 'enum' || data['type'] === 'enums' || data['type'] === 'dictionary'; },
        },
        subForm: {
            name: "Form",
            to: () => types['forms'],
            type: "relation",
            relativeTo: ['flowDef'],
            visibility: (data) => { return data['type'] === 'form'; },
        },
        formStyle: {
            name: "Form Style",
            type: "enum",
            values: {
                "table": {
                    name: "Table form"
                },
                "vertical": {
                    name: "Vertical form"
                },
                "editable": {
                    name: "Editable form"
                },
            },
            visibility: (data) => { return data['type'] === 'form'; },
        },
        author: {
            name: "Author",
            type: "relation",
            to: () => types['users'],
            userEditable: false,
        },
        updatedAt: {
            name: "Updated At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        createdAt: {
            name: "Created At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        }
    }; },
});

export const FLOW_OPTION_DEF_OPTIONS_KEY = 'flowOptionDefs';
